
import { useState } from "react"
import { useAtomValue } from "jotai"
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Alert from '@mui/material/Alert'
import CloseRoundedIcon from "@mui/icons-material/CloseRounded"
import TextField from "@mui/material/TextField"
import InputAdornment from '@mui/material/InputAdornment'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'

import { UserRole } from '@/types'
import { selectedCollectionPointIdAtom } from "@/state"
import { useDisablePublicUrl, useGeneratePublicUrl, useGetPublicUrl } from "@/hooks/api/use-collection-point"
import PermissionsGate from "@/components/permissions-gate"


export default function CollectionPointPublicLink() {

    const collectionPointId = useAtomValue(selectedCollectionPointIdAtom)

    const [success, setSuccess] = useState<string | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [requestInProgress, setRequestInProgress] = useState<boolean>(false)

    const { data } = useGetPublicUrl(collectionPointId)

    const mutation = useGeneratePublicUrl(
        collectionPointId,
        {
            setRequestInProgress,
            onErrorCallback(errorMessage) {
                setError(errorMessage)
            },
            onSuccessCallback(message) {
                setSuccess(message)
            }
        },
    )

    const mutationDisable = useDisablePublicUrl(
        collectionPointId,
        {
            setRequestInProgress,
            onErrorCallback(errorMessage) {
                setError(errorMessage)
            },
            onSuccessCallback(message) {
                setSuccess(message)
            }
        },
    )

    const handleClickGenerate = () => {
        setRequestInProgress(true)
        setError(null)
        setSuccess(null)
        mutation.mutate()
    }

    const handleClickDisable = () => {
        setRequestInProgress(true)
        setError(null)
        setSuccess(null)
        mutationDisable.mutate()
    }

    const publicUrl = data?.public_url

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
        }}>
            {publicUrl ?
                <TextField
                    value={publicUrl}
                    label="Přístupový odkaz"
                    autoComplete="off"
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="Zkopírovat do schránky"
                                    onClick={() => navigator.clipboard.writeText(publicUrl)}
                                >
                                    <ContentCopyRoundedIcon />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
                :
                <Box>Nemáte žádný aktivní odkaz</Box>
            }
            <PermissionsGate userRoles={[
                UserRole.admin,
                UserRole.collectionPointAdmin,
                UserRole.collectionCompany,
                UserRole.municipalityOfficial,
            ]}>
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                }}>
                    <Button
                        variant="outlined"
                        color="terciary"
                        onClick={() => handleClickGenerate()}
                        disabled={requestInProgress}
                    >
                        {publicUrl ? 'Vygenerovat nový' : 'Vygenerovat odkaz'}
                    </Button>
                    {publicUrl &&
                        <Button
                            variant="outlined"
                            color="terciary"
                            onClick={() => handleClickDisable()}
                            disabled={requestInProgress}
                        >
                            Deaktivovat
                        </Button>
                    }
                </Box>
            </PermissionsGate>
            <Box>
                {success &&
                    <Alert
                        severity="success"
                        sx={(theme) => ({
                            flexGrow: 1,
                            margin: theme.spacing(1, 0),
                        })}
                        action={
                            <IconButton
                                color="success"
                                size="small"
                                onClick={() => {
                                    setSuccess(null)
                                }}
                            >
                                <CloseRoundedIcon />
                            </IconButton>
                        }
                    >
                        {success}
                    </Alert>
                }
                {error &&
                    <Alert
                        severity="error"
                        sx={(theme) => ({
                            flexGrow: 1,
                            margin: theme.spacing(1, 0),
                        })}
                        action={
                            <IconButton
                                color="error"
                                size="small"
                                onClick={() => {
                                    setError(null)
                                }}
                            >
                                <CloseRoundedIcon />
                            </IconButton>
                        }
                    >
                        {error}
                    </Alert>
                }
            </Box>
        </Box>
    )
}
